import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, addDoc } from 'firebase/firestore';
import emailjs from '@emailjs/browser';
import { db } from '../firebase/config';
import { 
  EMAILJS_SERVICE_ID, 
  EMAILJS_TEMPLATE_ID_CONTACT,
  EMAILJS_TEMPLATE_ID, 
  EMAILJS_PUBLIC_KEY,
  RECIPIENT_EMAIL 
} from '../config/emailjs';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear status when user starts typing
    if (status) setStatus('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('');

    let emailSent = false;
    let firestoreSaved = false;

    try {
      // Step 1: Save to Firestore
      try {
        await addDoc(collection(db, 'contact'), {
          name: formData.name,
          email: formData.email,
          message: formData.message,
          timestamp: new Date()
        });
        firestoreSaved = true;
      } catch (firestoreError) {
        console.error('Firestore error: ', firestoreError);
        throw new Error('Failed to save your message. Please try again.');
      }

      // Step 2: Send notification email using EmailJS (if configured)
      const emailjsTemplateId = EMAILJS_TEMPLATE_ID_CONTACT || EMAILJS_TEMPLATE_ID;

      if (EMAILJS_SERVICE_ID && emailjsTemplateId && EMAILJS_PUBLIC_KEY) {
        try {
          await emailjs.send(
            EMAILJS_SERVICE_ID,
            emailjsTemplateId,
            {
              to_email: RECIPIENT_EMAIL,
              from_name: formData.name,
              from_email: formData.email,
              subject: `Contact Form Submission from ${formData.name}`,
              message: formData.message
            },
            EMAILJS_PUBLIC_KEY
          );
          emailSent = true;
          console.log('Contact form email sent successfully');
        } catch (emailError) {
          console.error('EmailJS error:', emailError);
          // Email failed but form is saved, show warning
          emailSent = false;
        }
      } else {
        // EmailJS not configured
        console.warn('EmailJS is not configured. Please add your EmailJS credentials to src/config/emailjs.js');
      }

      // Success message based on what happened
      if (firestoreSaved && emailSent) {
        setStatus('✓ Your message has been sent successfully! We will get back to you soon.');
      } else if (firestoreSaved && !emailSent) {
        setStatus('✓ Your message has been received. We will review it and get back to you soon.');
      }

      // Reset form
      setFormData({
        name: '',
        email: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form: ', error);
      setStatus(`✗ ${error.message || 'An error occurred. Please try again or contact us directly at support@nqcagency.com'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-container">
      <div className="contact-content">
        <div className="contact-main">
          <section className="contact-section">
            <h1 className="contact-title">Contact</h1>
            
            <div className="address-box">
              <div className="address-header">
                <strong>88 Pine St #148</strong>
              </div>
              <div className="address-text">
                88 Pine St #148, New York, NY 10005, USA
              </div>
              <div className="address-links">
                <a href="https://maps.google.com/?q=88+Pine+St+%23148,+New+York,+NY+10005" target="_blank" rel="noopener noreferrer" className="directions-link">
                  <span className="map-icon">📍</span> Directions
                </a>
                <a href="https://maps.google.com/?q=88+Pine+St+%23148,+New+York,+NY+10005" target="_blank" rel="noopener noreferrer" className="view-map-link">
                  View larger map
                </a>
              </div>
            </div>

            <div className="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3024.184133712123!2d-74.0086!3d40.7074!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c25a1655c0c5c1%3A0x8b8c8c8c8c8c8c8c!2s88%20Pine%20St%20%23148%2C%20New%20York%2C%20NY%2010005!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="NQC Agency Location"
              ></iframe>
            </div>

            <div className="contact-info">
              <div className="contact-info-item">
                <span className="contact-icon">🏠</span>
                <span>88 Pine St, Suite 148, Wall Street Plaza New York, NY 10005, United States of America</span>
              </div>
              <div className="contact-info-item">
                <span className="contact-icon">✉️</span>
                <a href="mailto:support@nqcagency.com">support@nqcagency.com</a>
              </div>
            </div>

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <textarea
                  name="message"
                  placeholder="Message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  className="form-textarea"
                ></textarea>
              </div>
              <button 
                type="submit" 
                className="submit-button"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'SEND'}
              </button>
              {status && (
                <div className={`contact-status ${status.includes('✗') || status.includes('error') || status.includes('Error') || status.includes('Failed') ? 'error' : 'success'}`}>
                  {status}
                </div>
              )}
            </form>
          </section>
        </div>

        <aside className="contact-sidebar">
          <div className="sidebar-section">
            <h2 className="sidebar-title">Our Services</h2>
            <ul className="sidebar-links">
              <li><Link to="/system-certification">System Certification</Link></li>
              <li><Link to="/product-certification">Product Certification</Link></li>
              <li><Link to="/quality-certification">Quality Certification</Link></li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Contact;

